/**
 * Inventory Migration Script
 *
 * This script will:
 * 1. Delete all existing inventory records
 * 2. Import new stock data from CSV
 * 3. Set up the new Stock and Movement collections structure
 *
 * Run this from the browser console or create a migration page
 */

import { collection, getDocs, doc, Timestamp, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';
import logger from './logger';

/**
 * Step 1: Delete all existing inventory records
 */
export const deleteExistingInventory = async () => {
  logger.info('Starting inventory deletion...');

  try {
    const inventoryRef = collection(db, 'inventory');
    const snapshot = await getDocs(inventoryRef);

    const totalDocs = snapshot.docs.length;
    logger.info(`Found ${totalDocs} inventory records to delete`);

    if (totalDocs === 0) {
      logger.info('No inventory records to delete');
      return { success: true, deleted: 0 };
    }

    // Delete in batches of 500 (Firestore batch limit)
    const batchSize = 500;
    let deletedCount = 0;

    for (let i = 0; i < snapshot.docs.length; i += batchSize) {
      const batch = writeBatch(db);
      const batchDocs = snapshot.docs.slice(i, i + batchSize);

      batchDocs.forEach((docSnapshot) => {
        batch.delete(doc(db, 'inventory', docSnapshot.id));
      });

      await batch.commit();
      deletedCount += batchDocs.length;
      logger.info(`Deleted ${deletedCount}/${totalDocs} records`);
    }

    logger.info('✅ All inventory records deleted successfully');
    return { success: true, deleted: deletedCount };

  } catch (error) {
    logger.error('❌ Error deleting inventory:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Step 2: Parse CSV data and import stock
 */
export const parseStockCSV = (csvText) => {
  const lines = csvText.trim().split('\n');
  const stockItems = [];

  // Skip header row (index 0)
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];

    // Skip empty lines or total line
    if (!line.trim() || line.includes(',,,,')) continue;

    // Parse CSV (handling quoted values with commas)
    const values = [];
    let currentValue = '';
    let inQuotes = false;

    for (let char of line) {
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        values.push(currentValue.trim());
        currentValue = '';
      } else {
        currentValue += char;
      }
    }
    values.push(currentValue.trim()); // Push last value

    if (values.length >= 7) {
      const [sn, item, partNumber, quantity, location, stockValue, total] = values;

      // Clean up numeric values (remove commas and spaces)
      const cleanNumber = (str) => {
        if (!str || str === '-' || str.trim() === '') return 0;
        return parseFloat(str.replace(/[,\s]/g, ''));
      };

      const stockItem = {
        serialNumber: sn,
        itemName: item,
        partNumber: partNumber,
        quantity: cleanNumber(quantity),
        location: location,
        unitCost: cleanNumber(stockValue),
        totalValue: cleanNumber(total),
        type: item.includes('OIL') ? 'consumable' : 'part',
        dateAdded: Timestamp.now(),
        lastUpdated: Timestamp.now(),
        reorderLevel: 5, // Default reorder level
        status: 'active'
      };

      stockItems.push(stockItem);
    }
  }

  return stockItems;
};

/**
 * Step 3: Import stock data to new 'stock' collection
 */
export const importStockData = async (stockItems) => {
  logger.info(`Starting import of ${stockItems.length} stock items...`);

  try {
    const stockRef = collection(db, 'stock');
    let importedCount = 0;

    // Import in batches
    const batchSize = 500;

    for (let i = 0; i < stockItems.length; i += batchSize) {
      const batch = writeBatch(db);
      const batchItems = stockItems.slice(i, i + batchSize);

      for (const item of batchItems) {
        const docRef = doc(stockRef);
        batch.set(docRef, item);
      }

      await batch.commit();
      importedCount += batchItems.length;
      logger.info(`Imported ${importedCount}/${stockItems.length} items`);
    }

    logger.info('✅ All stock items imported successfully');
    return { success: true, imported: importedCount };

  } catch (error) {
    logger.error('❌ Error importing stock:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Step 4: Create initial movement record for opening stock
 */
export const createOpeningStockMovements = async (stockItems) => {
  logger.info('Creating opening stock movement records...');

  try {
    const movementRef = collection(db, 'movements');
    let createdCount = 0;

    const batchSize = 500;

    for (let i = 0; i < stockItems.length; i += batchSize) {
      const batch = writeBatch(db);
      const batchItems = stockItems.slice(i, i + batchSize);

      for (const item of batchItems) {
        if (item.quantity > 0) {
          const movement = {
            itemCode: item.partNumber,
            itemDescription: item.itemName,
            movementType: 'IN',
            transactionType: 'OPENING_STOCK',
            quantityIn: item.quantity,
            quantityOut: 0,
            balanceAfter: item.quantity,
            unitCost: item.unitCost,
            totalValue: item.totalValue,
            date: Timestamp.now(),
            supplier: 'Opening Stock',
            remarks: 'Initial stock import',
            recordedBy: 'System',
            location: item.location
          };

          const docRef = doc(movementRef);
          batch.set(docRef, movement);
        }
      }

      await batch.commit();
      createdCount += batchItems.length;
      logger.info(`Created ${createdCount}/${stockItems.length} movement records`);
    }

    logger.info('✅ Opening stock movements created successfully');
    return { success: true, created: createdCount };

  } catch (error) {
    logger.error('❌ Error creating movements:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Main migration function - Run this to execute the full migration
 */
export const runFullMigration = async (csvText) => {
  logger.info('🚀 Starting Full Inventory Migration...\n');

  try {
    // Step 1: Delete existing inventory
    logger.info('STEP 1: Deleting existing inventory...');
    const deleteResult = await deleteExistingInventory();
    if (!deleteResult.success) {
      throw new Error(`Deletion failed: ${deleteResult.error}`);
    }
    logger.info(`✅ Deleted ${deleteResult.deleted} records\n`);

    // Step 2: Parse CSV data
    logger.info('STEP 2: Parsing CSV data...');
    const stockItems = parseStockCSV(csvText);
    logger.info(`✅ Parsed ${stockItems.length} items\n`);

    // Step 3: Import to stock collection
    logger.info('STEP 3: Importing stock data...');
    const importResult = await importStockData(stockItems);
    if (!importResult.success) {
      throw new Error(`Import failed: ${importResult.error}`);
    }
    logger.info(`✅ Imported ${importResult.imported} items\n`);

    // Step 4: Create opening stock movements
    logger.info('STEP 4: Creating movement records...');
    const movementResult = await createOpeningStockMovements(stockItems);
    if (!movementResult.success) {
      throw new Error(`Movement creation failed: ${movementResult.error}`);
    }
    logger.info(`✅ Created ${movementResult.created} movement records\n`);

    logger.info('🎉 MIGRATION COMPLETED SUCCESSFULLY!');
    return {
      success: true,
      summary: {
        deleted: deleteResult.deleted,
        imported: importResult.imported,
        movementsCreated: movementResult.created
      }
    };

  } catch (error) {
    logger.error('❌ MIGRATION FAILED:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

const inventoryMigration = {
  deleteExistingInventory,
  parseStockCSV,
  importStockData,
  createOpeningStockMovements,
  runFullMigration
};

export default inventoryMigration;
